import {auth} from "../firebaseConfig";
import {useEffect} from "react";
import {useAppStore} from "../store/appStore";
import {useAuthStore} from "../store/authStore";
import {addBreadcrumb} from "../utils/auth";

/**
 * Firebase authentication tokens have a lifespan of 1 hour.
 * Firebase promises automatic token refresh shortly before expiration.
 * However, this refresh mechanism is lazy, meaning that if no Firebase methods
 * are called, the token will not be refreshed automatically.
 *
 * Therefor we need to schedule a token refresh every 55 minutes.
 * This will ensure that the token is always fresh.
 */
const SAFETY_BUFFER = 5 * 60 * 1000; // 5 min
const RETRY_TIMEOUT = 60 * 1000; // 1 min
const ONE_HOUR = 3600000;

export const useFirebaseTokenRefresh = () => {
    const isIntegration = useAppStore((state) => state.isIntegration);
    const tokenReady = useAuthStore(state => state.tokenReady);

    useEffect(() => {
        let refreshTimeout: NodeJS.Timeout;

        function reSchedule(ms?: number) {
            addBreadcrumb('Token refresh scheduled', {ms})
            clearTimeout(refreshTimeout);
            refreshTimeout = setTimeout(scheduleRefresh, ms);
        }

        const scheduleRefresh = async () => {
            try {
                if (isIntegration) {
                    console.info('[Token Refresh] In integration mode. Disable refresh');
                    addBreadcrumb('[Token Refresh] Token refresh disabled')
                    return;
                }

                const user = auth.currentUser;
                if (!user || !tokenReady) {
                    console.info('[Token Refresh] Token or user is not ready. Retry after (min):', RETRY_TIMEOUT / 60000);
                    addBreadcrumb('[Token Refresh] Token or user not ready')
                    reSchedule(RETRY_TIMEOUT);
                    return;
                }

                const tokenResult = await user.getIdTokenResult();
                const expirationTime = new Date(tokenResult.expirationTime).getTime();
                const now = Date.now();

                let timeUntilExpiration = expirationTime - now;
                console.info('[Token Refresh] Time until expiration (min):', Math.ceil(timeUntilExpiration / 60000));

                let refreshDelay = timeUntilExpiration - SAFETY_BUFFER;
                console.info('[Token Refresh] Refresh delay (min):', Math.ceil(refreshDelay / 60000));
                addBreadcrumb('[Token Refresh] Token refresh', { expirationTime, timeUntilExpiration, refreshDelay})

                if (refreshDelay <= 0) {
                    await user.getIdToken(true);
                    addBreadcrumb('[Token Refresh] Emergency token refresh')
                    console.info("[Token Refresh] Emergency token refresh");
                    refreshDelay = ONE_HOUR - SAFETY_BUFFER;
                }
                reSchedule(refreshDelay)
            } catch (error) {
                console.warn("[Token Refresh] Refresh error, retrying...", error);
                reSchedule(RETRY_TIMEOUT);
            }
        };

        scheduleRefresh();

        return () => {
            clearTimeout(refreshTimeout);
        };
    }, [isIntegration, tokenReady]);
};
